import { useEffect, useMemo, useState } from 'react';
import useStaffSession from '../hooks/useStaffSession';
import StaffSignIn from '../components/StaffSignIn';
import { useAuth } from '../hooks/useAuth';
import { leavePreview } from '../lib/preview';
import { previewApi } from '../lib/api';
import { startPageUrl } from '../lib/sites';

const SHOWN_WITHOUT_SEARCH = 60;

// Staff opening the ordering site (orders.silverservice.com/#staff-preview):
// pick a customer and see the portal exactly as they do — their prices,
// order guide and order history. The preview is look-only; the server
// refuses every order and change made from it (backend lib/staffPreview.js).
export default function StaffPreview() {
  const session = useStaffSession('orders');
  const { user } = useAuth();
  const [customers, setCustomers] = useState(null);
  const [query, setQuery] = useState('');
  const [opening, setOpening] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const previous = document.title;
    document.title = 'Ordering · staff preview';
    return () => { document.title = previous; };
  }, []);

  useEffect(() => {
    if (!session.token) return undefined;
    let cancelled = false;
    previewApi.customers()
      .then((r) => { if (!cancelled) setCustomers(r.customers || []); })
      .catch((e) => { if (!cancelled) setError(e.message || "Couldn't load customers."); });
    return () => { cancelled = true; };
  }, [session.token]);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    const all = customers || [];
    return q ? all.filter((c) => c.name.toLowerCase().includes(q) || (c.salesRepName || '').toLowerCase().includes(q)) : all;
  }, [customers, query]);

  if (!session.token) return <StaffSignIn session={session} siteName="Ordering" />;

  async function open(customer) {
    setOpening(customer.id);
    setError('');
    try {
      const { token } = await previewApi.start(customer.id);
      leavePreview(user);
      localStorage.setItem('ss_token', token);
      // Without the #fragment this is a full load, so the portal starts
      // fresh as this customer.
      window.location.replace(window.location.pathname + window.location.search);
    } catch (e) {
      setError(e.message || "Couldn't open that customer.");
      setOpening('');
    }
  }

  const staffName = tokenName(session.token);
  const shown = query.trim() ? matches : matches.slice(0, SHOWN_WITHOUT_SEARCH);
  const current = user?.preview ? user.customerName : '';

  return (
    <div style={s.page}>
      <div style={s.container}>
        <header style={s.header}>
          <img src="/logo.png" alt="Silver Service" style={s.logo} />
          <a href={startPageUrl()} style={s.quietLink}>All staff sites</a>
        </header>

        <h1 style={s.h1}>See the ordering site as a customer</h1>
        <p style={s.lead}>
          Pick a customer to see the portal exactly as they do: their prices, order guide, order history and invoices.
          It's look-only. Nothing can be ordered, sent or changed from a preview.
        </p>
        {staffName && <p style={s.who}>Signed in as <strong>{staffName}</strong></p>}

        {current && (
          <div style={s.current}>
            <span>You're viewing as <strong>{current}</strong>.</span>
            <a href={window.location.pathname} style={s.currentLink}>Back to {current}</a>
          </div>
        )}

        <input
          style={s.search}
          type="search"
          placeholder="Search customers or sales reps"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Search customers"
          autoFocus
        />
        {error && <div style={s.err}>{error}</div>}

        {customers === null && !error && <p style={s.quiet}>Loading customers…</p>}
        {customers && (
          <>
            <p style={s.count}>
              {query.trim()
                ? `${matches.length} match${matches.length === 1 ? '' : 'es'}`
                : `${customers.length} customers${customers.length > SHOWN_WITHOUT_SEARCH ? `, first ${SHOWN_WITHOUT_SEARCH} shown. Type to find the rest` : ''}`}
            </p>
            <ul style={s.list}>
              {shown.map((c) => (
                <li key={c.id}>
                  <button type="button" style={s.row} onClick={() => open(c)} disabled={!!opening}>
                    <span style={s.name}>{c.name}</span>
                    <span style={s.meta}>
                      {c.usesPortal && <span style={s.badge}>Uses the portal</span>}
                      {c.salesRepName && <span>Rep: {c.salesRepName}</span>}
                      <span style={s.idText}>#{c.id}</span>
                    </span>
                    {opening === c.id && <span style={s.opening}>Opening…</span>}
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  );
}

function tokenName(token) {
  try {
    const body = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const claims = JSON.parse(atob(body));
    return claims.name || claims.email || '';
  } catch {
    return '';
  }
}

const RED = '#A50B18';
const s = {
  page: { minHeight: '100vh', background: '#f5f5f5', padding: '24px 16px 56px', boxSizing: 'border-box', fontFamily: "'Inter', sans-serif", color: '#333' },
  container: { maxWidth: 760, margin: '0 auto' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 24 },
  logo: { height: 44 },
  quietLink: { color: '#777', fontSize: '0.85rem' },
  h1: { fontSize: '1.4rem', fontWeight: 700, color: '#222', margin: '0 0 8px' },
  lead: { color: '#555', fontSize: '0.95rem', lineHeight: 1.5, margin: '0 0 6px' },
  who: { color: '#777', fontSize: '0.85rem', margin: '0 0 18px' },
  current: { display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 8, background: '#fff8e6', border: '1px solid #f0d9a6', borderRadius: 10, padding: '10px 14px', fontSize: '0.9rem', marginBottom: 16 },
  currentLink: { color: RED, fontWeight: 600 },
  search: { width: '100%', boxSizing: 'border-box', padding: '0.75rem 0.95rem', borderRadius: 10, border: '1px solid #ddd', fontSize: '1rem', outline: 'none', background: '#fff' },
  err: { color: '#c0392b', fontSize: '0.88rem', background: 'rgba(231,76,60,0.08)', border: '1px solid rgba(231,76,60,0.2)', borderRadius: 8, padding: '0.6rem 0.85rem', marginTop: 12 },
  quiet: { color: '#888', fontSize: '0.92rem' },
  count: { color: '#888', fontSize: '0.82rem', margin: '12px 2px 8px' },
  list: { listStyle: 'none', margin: 0, padding: 0, background: '#fff', border: '1px solid #e3e3e3', borderRadius: 12, overflow: 'hidden' },
  row: { display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '4px 12px', width: '100%', textAlign: 'left', background: 'none', border: 'none', borderBottom: '1px solid #f0f0f0', padding: '12px 16px', cursor: 'pointer', font: 'inherit', color: 'inherit' },
  name: { flex: '1 1 220px', fontWeight: 600, color: '#222', fontSize: '0.95rem' },
  meta: { display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '4px 10px', color: '#777', fontSize: '0.82rem' },
  badge: { color: '#2d6a3e', background: '#eef7f0', border: '1px solid #cfe6d5', borderRadius: 999, padding: '1px 8px', fontSize: '0.75rem', fontWeight: 600 },
  idText: { color: '#aaa' },
  opening: { color: RED, fontSize: '0.82rem', fontWeight: 600 },
};
